import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import type {ListItemViewModel} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {SkillChatMode} from 'llm/chat-library/skills/SkillActionsMenu';
import {canManageSkillAccess, downloadSkillBundle, SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import EntityStore from 'stores/EntityStore';
import type {MantineOverflowMenuOption} from 'ui-library/menus/MantineOverflowMenu';

/**
 * Maps a domain SkillSummary into the shared list row view-model. All per-skill behavior (toggle
 * gating, which menu items appear) lives here as plain data + closures — the shared row component
 * stays generic. This is the "ViewModel" seam: `SkillSummary` (Model) -> `ListItemViewModel`.
 *
 * The overflow menu mirrors {@link SkillActionsMenu} (the menu shown in the legacy skills list and
 * the detail view) so a skill surfaces the same actions everywhere: Try with chat, Edit with chat
 * (if editable), Download skill, Manage access (if the user can manage sharing), Archive (if
 * archivable), and Export audit log. All items live in a single section to match that menu's flat,
 * divider-less dropdown.
 */
export function toSkillListItem(
  skill: SkillSummary,
  handlers: {
    onToggleEnabled: (skill: SkillSummary, enabled: boolean) => Promise<void>;
    onTryWithChat: (skill: SkillSummary, mode: SkillChatMode) => void;
    onManageAccess: (skill: SkillSummary) => void;
    onArchive: (skill: SkillSummary) => void;
  }
): ListItemViewModel {
  const isTenantAdmin = EntityStore.currentUser()?.isTenantAdmin ?? false;
  // Mirrors SkillActionsMenu: extension-installed skills can't be archived; otherwise
  // admins/owners and tenant admins can.
  const canArchive = skill.extensionDefinitionId == null && (skill.canManageAccess || isTenantAdmin);
  // Only surface "Edit with chat" when the user actually has write access to the skill.
  const canEditWithChat = skill.canEdit;
  const canManageSharing = canManageSkillAccess(skill);

  const items: Array<MantineOverflowMenuOption> = [
    {
      name: 'Try with chat',
      onSelect: () => {
        handlers.onTryWithChat(skill, SkillChatMode.TRY);
      },
    },
  ];
  if (canEditWithChat) {
    items.push({
      name: 'Edit with chat',
      onSelect: () => {
        handlers.onTryWithChat(skill, SkillChatMode.EDIT);
      },
    });
  }
  items.push({
    name: 'Download skill',
    onSelect: () => {
      downloadSkillBundle(skill.id);
    },
  });
  if (canManageSharing) {
    items.push({
      name: 'Manage access',
      onSelect: () => {
        handlers.onManageAccess(skill);
      },
    });
  }
  if (canArchive) {
    items.push({
      name: 'Archive',
      variant: 'destructive',
      onSelect: () => {
        handlers.onArchive(skill);
      },
    });
  }
  items.push({
    name: EXPORT_AUDIT_LOG_DISPLAY_STRING,
    onSelect: () => {
      exportAuditLogs([skill.id]);
    },
  });

  return {
    id: skill.id,
    title: skill.name,
    // Byline + clamped description, matching the existing SkillCard layout.
    subtitle: skill.creatorName != null ? `by ${skill.creatorName}` : undefined,
    description: skill.description ?? undefined,
    toggle: {
      checked: skill.isEnabled,
      ariaLabel: skill.isEnabled ? `Disable ${skill.name}` : `Enable ${skill.name}`,
      tooltip: skill.isEnabled
        ? 'Disable skill (agents will not use it in your chats)'
        : 'Enable skill (agents will use it in your chats)',
      onChange: (enabled) => handlers.onToggleEnabled(skill, enabled),
    },
    menu: [{items}],
  };
}
